import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { CopyResultButton } from "@/components/CopyResultButton";

export default function MolesToMassConverter() {
  const [moles, setMoles] = useState("");
  const [molarMass, setMolarMass] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculate = () => {
    const n = parseFloat(moles);
    const mm = parseFloat(molarMass);

    if (isNaN(n) || isNaN(mm) || n < 0 || mm <= 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid positive numbers.",
        variant: "destructive",
      });
      return;
    }

    const mass = n * mm;
    setResult(mass);

    toast({
      title: "Calculation Complete",
      description: `Mass: ${mass.toFixed(4)} g`,
    });
  };

  const reset = () => {
    setMoles("");
    setMolarMass("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Moles to Mass Converter" 
      calculatorPath="/calculator/moles-to-mass"
      category="chemistry"
      subcategory="Stoichiometry"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Moles to Mass Converter</CardTitle>
            <CardDescription>
              Convert moles to mass using molar mass
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="moles">Moles (mol)</Label>
                <Input
                  id="moles"
                  type="number"
                  step="any"
                  value={moles}
                  onChange={(e) => setMoles(e.target.value)}
                  placeholder="Enter moles"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="molarMass">Molar Mass (g/mol)</Label>
                <Input
                  id="molarMass"
                  type="number"
                  step="any"
                  value={molarMass}
                  onChange={(e) => setMolarMass(e.target.value)}
                  placeholder="Enter molar mass"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result !== null && (
              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">Result</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start justify-between gap-4">
                    <p className="text-2xl font-bold break-all overflow-wrap-anywhere flex-1">{result.toFixed(4)} g</p>
                    <CopyResultButton value={`${result.toFixed(4)} g`} />
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Formula</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-mono">m = n × M</p>
                <p className="text-sm text-muted-foreground mt-2">
                  where m = mass (g), n = moles, M = molar mass (g/mol)
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
